import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import {
  ChevronLeft, ChevronRight, Clock, Globe, Calendar, Loader2,
  AlertCircle, CheckCircle2
} from 'lucide-react';
import {
  format, addMonths, subMonths, startOfMonth, endOfMonth,
  eachDayOfInterval, isSameMonth, isSameDay, isToday, isBefore, startOfDay
} from 'date-fns';
import { Button } from '../components/ui/Button';

interface TimeSlot {
  startTime: string;
  endTime: string;
  available: boolean;
}

interface DayAvailability {
  date: string;
  slots: TimeSlot[];
  isAvailable: boolean;
}

interface BookingInfo {
  id: string;
  attendeeName: string;
  attendeeEmail: string;
  startTime: string;
  endTime: string;
  timezone: string;
  page: {
    id: string;
    title: string;
    slug: string;
    duration: number;
    timezone: string;
    color?: string;
  };
}

export default function RescheduleBooking() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [bookingInfo, setBookingInfo] = useState<BookingInfo | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availability, setAvailability] = useState<DayAvailability[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (slug && token) {
      loadBookingInfo();
    } else {
      setError('Invalid reschedule link.');
      setLoading(false);
    }
  }, [slug, token]);

  useEffect(() => {
    if (bookingInfo) {
      loadAvailability(currentMonth);
    }
  }, [currentMonth, bookingInfo]);

  const loadBookingInfo = async () => {
    try {
      setLoading(true);
      const data = await fetch(
        `${(import.meta as any).env.VITE_API_URL || 'http://localhost:3035'}/api/v1/scheduling/public/${slug}/reschedule?token=${token}`
      ).then(r => r.json()) as { booking?: BookingInfo; error?: string };

      if (data.error) {
        setError(data.error);
      } else if (data.booking) {
        setBookingInfo(data.booking);
      }
    } catch {
      setError('Failed to load booking information.');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailability = async (month: Date) => {
    if (!bookingInfo) return;
    try {
      const start = format(startOfMonth(month), 'yyyy-MM-dd');
      const end = format(endOfMonth(month), 'yyyy-MM-dd');
      const data = await api.getPublicAvailability(bookingInfo.page.slug, start, end) as { availability: DayAvailability[] };
      setAvailability(data.availability || []);
    } catch {
      console.error('Failed to load availability');
    }
  };

  const handleSubmit = async () => {
    if (!bookingInfo || !selectedDate || !selectedSlot) return;

    try {
      setSubmitting(true);
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await fetch(
        `${(import.meta as any).env.VITE_API_URL || 'http://localhost:3035'}/api/v1/scheduling/public/${slug}/reschedule/${bookingInfo.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            date: dateStr,
            startTime: selectedSlot.startTime,
            token,
          }),
        }
      ).then(r => r.json()) as { error?: string };

      if (response.error) {
        setError(response.error);
      } else {
        setSuccess(true);
      }
    } catch {
      setError('Failed to reschedule booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getDayAvailability = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return availability.find(a => a.date === dateStr);
  };

  const selectedDayAvailability = selectedDate ? getDayAvailability(selectedDate) : null;
  const themeColor = bookingInfo?.page.color || '#2563eb';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-red-200 p-8 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Reschedule</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-green-200 p-8 text-center">
          <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Booking Rescheduled!</h2>
          <p className="text-gray-600">
            Your meeting has been rescheduled to{' '}
            <strong>{selectedDate && format(selectedDate, 'MMMM d, yyyy')}</strong> at{' '}
            <strong>{selectedSlot?.startTime}</strong>.
          </p>
          <p className="text-sm text-gray-500 mt-4">A confirmation email has been sent to {bookingInfo?.attendeeEmail}.</p>
        </div>
      </div>
    );
  }

  if (!bookingInfo) return null;

  const calendarDays = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  });

  const startDayOfWeek = startOfMonth(currentMonth).getDay();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center space-x-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${themeColor}20` }}
            >
              <Calendar className="w-6 h-6" style={{ color: themeColor }} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{bookingInfo.page.title}</h1>
              <p className="text-sm text-gray-500">
                Reschedule your booking — currently{' '}
                {format(new Date(bookingInfo.startTime), 'MMM d, yyyy h:mm a')}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4 mt-4 text-sm text-gray-600">
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-1 text-gray-400" />
              {bookingInfo.page.duration} minutes
            </div>
            <div className="flex items-center">
              <Globe className="w-4 h-4 mr-1 text-gray-400" />
              {bookingInfo.timezone}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Calendar */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                {format(currentMonth, 'MMMM yyyy')}
              </h2>
              <div className="flex space-x-1">
                <button
                  onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 mb-2">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                <div key={d} className="text-center text-xs font-medium text-gray-400 py-2">{d}</div>
              ))}
            </div>

            <div className="grid grid-cols-7">
              {Array.from({ length: startDayOfWeek }).map((_, i) => (
                <div key={`empty-${i}`} />
              ))}
              {calendarDays.map(day => {
                const dayAvail = getDayAvailability(day);
                const isAvailable = dayAvail?.isAvailable && dayAvail.slots.length > 0;
                const isPast = isBefore(day, startOfDay(new Date()));
                const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
                const isCurrentMonth = isSameMonth(day, currentMonth);

                return (
                  <button
                    key={day.toISOString()}
                    disabled={!isAvailable || isPast || !isCurrentMonth}
                    onClick={() => {
                      setSelectedDate(day);
                      setSelectedSlot(null);
                    }}
                    className={`
                      aspect-square flex items-center justify-center text-sm rounded-full m-0.5 transition-all
                      ${!isCurrentMonth ? 'text-gray-200' : ''}
                      ${isPast || !isAvailable ? 'text-gray-300 cursor-not-allowed' : 'cursor-pointer'}
                      ${isSelected ? 'text-white font-semibold' : ''}
                      ${isAvailable && !isPast && !isSelected ? 'hover:opacity-80 font-medium' : ''}
                      ${isToday(day) && !isSelected ? 'ring-2 ring-offset-1' : ''}
                    `}
                    style={isSelected ? { backgroundColor: themeColor } : isAvailable && !isPast && isCurrentMonth ? { color: themeColor } : {}}
                  >
                    {format(day, 'd')}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time Slots */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            {selectedDate ? (
              <>
                <h3 className="font-semibold text-gray-900 mb-4">
                  {format(selectedDate, 'EEEE, MMMM d')}
                </h3>
                {selectedDayAvailability?.slots.length ? (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {selectedDayAvailability.slots.map((slot, i) => (
                      <button
                        key={i}
                        onClick={() => setSelectedSlot(slot)}
                        className={`w-full px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                          selectedSlot?.startTime === slot.startTime
                            ? 'border-current text-white'
                            : 'border-gray-200 text-gray-700 hover:border-current hover:text-current'
                        }`}
                        style={
                          selectedSlot?.startTime === slot.startTime
                            ? { backgroundColor: themeColor, borderColor: themeColor }
                            : {}
                        }
                      >
                        {slot.startTime} – {slot.endTime}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No available slots on this day.</p>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <Calendar className="w-10 h-10 text-gray-300 mb-3" />
                <p className="text-gray-500 text-sm">Select a date to see available times</p>
              </div>
            )}
          </div>
        </div>

        {selectedDate && selectedSlot && (
          <div className="mt-6 bg-white rounded-2xl shadow-sm border border-gray-200 p-6 flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">
                {format(selectedDate, 'EEEE, MMMM d')} at {selectedSlot.startTime}
              </p>
              <p className="text-sm text-gray-500">{bookingInfo.page.duration} minutes · {bookingInfo.timezone}</p>
            </div>
            <Button
              onClick={handleSubmit}
              isLoading={submitting}
              style={{ backgroundColor: themeColor, borderColor: themeColor }}
            >
              Confirm Reschedule
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
